import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  DATE_FORMAT,
  EEntityType,
  EOrderEventType,
  EPriceType,
  ERelationType,
  EStatusChipType,
  ILogpayPaymentMethod,
  INormalText,
  IParsedBooking,
  IParsedOrder,
  isNoFurtherInThePastThan,
  PaymentMethod,
  paymentTypeToRelationType,
  SIX_MONTHS,
  THIRTY_DAYS,
} from '@web/dtos';
import { SectionHeaderComponent } from '../../../../components/layout/section/section-header/section-header.component';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { UserStore } from '../../../../services/api/user/user/user-store.service';
import { DatePipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import { UserOrdersStore } from '../../../../services/api/orders/user-orders-store.service';
import { TextInputComponent } from '../../../../components/data-input/form-inputs/text-input/text-input.component';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { CentsToEurosPipe } from '../../../../pipes/cents-to-euros/cents-to-euros.pipe';
import { DecimalsToCentsPipe } from '../../../../pipes/decimals-to-cents/decimals-to-cents.pipe';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { OrderStatusChipComponent } from '../../../../components/data-display/order-status-chip/order-status-chip.component';
import { DateFormatSupport } from '../../../../services/support/date-format-support/date-format-support.service';
import { ConfirmationEmailGateway } from '../../../../services/api/confirmMail/confirmationEmail.gateway';
import { OrderHistorySubMenuComponent } from '../../../../components/feedback/order-history-sub-menu/order-history-sub-menu.component';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { ReportDesignerGateway } from '../../../../services/api/report-designer/report-designer.gateway';

export enum EOrderHistoryAction {
  resend = 0,
  download = 1,
}

@Component({
  selector: 'aw-account-orders-page',
  imports: [
    SectionComponent,
    ReactiveFormsModule,
    SectionHeaderComponent,
    NgTemplateOutlet,
    DatePipe,
    TextInputComponent,
    NgStyle,
    FilterTextsPipe,
    LanguagePipe,
    CentsToEurosPipe,
    DecimalsToCentsPipe,
    LoadingAnimationComponent,
    OrderStatusChipComponent,
    OrderHistorySubMenuComponent,
    AppendTestIdPipe,
  ],
  providers: [ConfirmationEmailGateway, ReportDesignerGateway],
  templateUrl: './account-orders-page.component.html',
})
export class AccountOrdersPageComponent {
  private readonly ordersStore: UserOrdersStore = inject(UserOrdersStore);
  private readonly userStore: UserStore = inject(UserStore);
  private readonly textStore: WebTextStore = inject(WebTextStore);
  public readonly openedProductId: WritableSignal<string | null> = signal<
    string | null
  >(null);
  public readonly ticketName: InputSignal<string> = input.required();
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  protected readonly corporateDesign: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly dateFormatSupport: DateFormatSupport =
    inject(DateFormatSupport);

  protected readonly isSubMenuVisible: WritableSignal<boolean> = signal(false);

  private readonly mailGateway: ConfirmationEmailGateway = inject(
    ConfirmationEmailGateway
  );

  private readonly reportDesignerGateway: ReportDesignerGateway = inject(
    ReportDesignerGateway
  );

  private readonly elementRef: ElementRef = inject(ElementRef);

  private readonly languagePipe: LanguagePipe = new LanguagePipe();
  private readonly filterTextPipe: FilterTextsPipe = new FilterTextsPipe();
  private readonly decimalsToCentsPipe: DecimalsToCentsPipe =
    new DecimalsToCentsPipe();
  private readonly centsToEurosPipe: CentsToEurosPipe = new CentsToEurosPipe();

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly EStatusChipType: typeof EStatusChipType = EStatusChipType;
  protected readonly DATE_FORMAT: string = DATE_FORMAT;

  protected readonly filterOptions: ERelationType[] = [
    ERelationType.thirtyDays,
    ERelationType.sixMonths,
    ERelationType.allTime,
  ];

  protected readonly texts: Signal<INormalText[]> = this.textStore.texts;
  protected readonly orders: Signal<IParsedOrder[]> =
    this.ordersStore.allUserOrders;
  protected readonly shownOrders: Signal<IParsedOrder[]> = computed(
    (): IParsedOrder[] => {
      return this.ordersStore
        .allUserOrders()
        .filter((order: IParsedOrder) =>
          JSON.stringify(order)
            .toLowerCase()
            .includes(this.searchValue()?.toLowerCase() ?? '')
        )
        .filter((order: IParsedOrder) => {
          switch (this.filterValue()) {
            case ERelationType.thirtyDays:
              return isNoFurtherInThePastThan(
                new Date(order.createdAt),
                THIRTY_DAYS
              );
            case ERelationType.sixMonths:
              return isNoFurtherInThePastThan(
                new Date(order.createdAt),
                SIX_MONTHS
              );
          }
          return true;
        })
        .sort((a: IParsedOrder, b: IParsedOrder): number => {
          return a.createdAt < b.createdAt ? 1 : 0;
        });
    }
  );
  protected readonly isLoading: Signal<boolean> = this.ordersStore.isLoading;
  protected readonly searchControl: FormControl<string> =
    this.formBuilder.nonNullable.control<string>('');
  protected readonly filterControl: FormControl<ERelationType> =
    this.formBuilder.nonNullable.control<ERelationType>(ERelationType.allTime);
  protected readonly searchValue: Signal<string | undefined> = toSignal(
    this.searchControl.valueChanges
  );
  protected readonly filterValue: Signal<ERelationType | undefined> = toSignal(
    this.filterControl.valueChanges
  );

  constructor() {
    effect(() => {
      const userOrGuestId: string = this.userStore.userOrGuestId();
      if (userOrGuestId) {
        this.ordersStore.getOrders(userOrGuestId);
      }
    });
  }

  protected nameForOrder(order: IParsedOrder): string {
    const onDemandProduct =
      order.onDemand?.items.find(item => item.type === EPriceType.ON_DEMAND)
        ?.product ?? order.onDemand?.items[0]?.product;

    if (onDemandProduct) {
      return onDemandProduct;
    }

    return (
      order.bookings
        ?.map((data: IParsedBooking) => {
          const texts: INormalText[] = data.marketingText || [];
          const marketingText = this.languagePipe.transform(
            this.filterTextPipe.transform(texts, ERelationType.title)
          );
          return marketingText || data.name;
        })
        .join(', ') || '-'
    );
  }

  protected shouldShowOnDemandCancellationDeadline(
    order: IParsedOrder
  ): boolean {
    return Boolean(
      order.onDemand?.journey?.cancellableUntil &&
      !order.events.some(event => event.eventType === EOrderEventType.refunded)
    );
  }

  protected totalPrice(orderData: IParsedBooking[]): string {
    const total: number = orderData.reduce(
      (acc, curr) => acc + (curr.grossPrice ?? 0),
      0
    );
    return total ? this.centsToEurosPipe.transform(total) : '--,-- €';
  }

  protected resolvePaymentMethod(paymentMethod: PaymentMethod): string {
    if ((paymentMethod as { type?: string })?.type === 'ON_DEMAND') {
      return '_';
    }
    const relationType: ERelationType | undefined = (
      paymentMethod as ILogpayPaymentMethod
    )?.type
      ? paymentTypeToRelationType((paymentMethod as ILogpayPaymentMethod).type)
      : undefined;

    if (relationType) {
      const translatedPayment: string = this.languagePipe.transform(
        this.filterTextPipe.transform(
          this.texts(),
          relationType,
          EEntityType.directTicket
        )
      );
      if (translatedPayment) {
        return translatedPayment;
      }
    }

    return (paymentMethod as ILogpayPaymentMethod)?.type || '_';
  }

  protected toggleSubMenu(productId: string): void {
    if (this.isSubMenuVisible() && this.openedProductId() === productId) {
      this.hideSubMenu();
    } else {
      this.showSubMenu(productId);
    }
  }

  protected showSubMenu(productId: string): void {
    this.openedProductId.set(productId);
    this.isSubMenuVisible.set(true);
  }

  protected hideSubMenu(): void {
    this.openedProductId.set(null);
    this.isSubMenuVisible.set(false);
  }

  protected handleSubMenuClick(
    action: EOrderHistoryAction,
    order: IParsedOrder
  ): void {
    switch (action) {
      case EOrderHistoryAction.resend:
        this.sendConfirmEmail(order);
        break;
      case EOrderHistoryAction.download:
        this.reportDesignerGateway
          .getProofOfPurchase(order.id)
          .subscribe((base64: string) => {
            this.downloadPdf(base64);
          });
        break;
    }
    this.hideSubMenu();
  }

  private downloadPdf(base64: string): void {
    const cleaned = base64.trim().replace(/^"|"$/g, '');
    const bytes = Uint8Array.from(atob(cleaned), c => c.charCodeAt(0));

    const blob = new Blob([bytes], {
      type: 'application/pdf',
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'proof-of-purchase.pdf';
    link.click();

    URL.revokeObjectURL(url);
  }

  protected changeFilter(orderFilter: ERelationType) {
    this.filterControl.setValue(orderFilter);
  }

  protected sendConfirmEmail(order: IParsedOrder): void {
    this.mailGateway.orderConfirmationEmail(order.id);
  }

  @HostListener('document:click', ['$event'])
  public onClick(event: MouseEvent): void {
    if (
      this.isSubMenuVisible() &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.hideSubMenu();
    }
  }
}
